import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import "./BtcTrading.css";

const API = "/api/demo-trading";
const STARTING_BALANCE = 10000;
const MIN_QTY = 0.001;
const STEP_QTY = 0.001;
const MAX_QTY = 0.1;

const money = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "$0.00";

  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const priceText = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "--";

  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const authHeaders = () => {
  const token = localStorage.getItem("token");

  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export default function BtcTrading() {
  const chartRef = useRef(null);
  const socketRef = useRef(null);

  const [price, setPrice] = useState(0);
  const [bid, setBid] = useState(0);
  const [ask, setAsk] = useState(0);

  const [account, setAccount] = useState({
    balance: STARTING_BALANCE,
    equity: STARTING_BALANCE,
    floatingProfit: 0,
    realizedProfit: 0,
  });

  const [trades, setTrades] = useState([]);
  const [history, setHistory] = useState([]);

  const [quantity, setQuantity] = useState(MIN_QTY);

  const [aiBotEnabled, setAiBotEnabled] = useState(false);
  const [aiBotSignal, setAiBotSignal] = useState(null);
  const [aiBotLoading, setAiBotLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [lastUpdated, setLastUpdated] = useState(null);

  /* =========================================================
     BTC QUOTE
  ========================================================= */

  const applyQuote = useCallback((quote) => {
    if (!quote) return;

    const nextPrice = Number(
      quote.price ??
      quote.last ??
      quote.lastPrice ??
      quote.close ??
      0
    );

    const nextBid = Number(quote.bid ?? nextPrice);
    const nextAsk = Number(quote.ask ?? nextPrice);

    if (nextPrice > 0) setPrice(nextPrice);
    if (nextBid > 0) setBid(nextBid);
    if (nextAsk > 0) setAsk(nextAsk);

    setLastUpdated(
      quote.lastUpdated
        ? new Date(quote.lastUpdated)
        : new Date()
    );
  }, []);

  /* =========================================================
     ACCOUNT
  ========================================================= */

  const loadAccount = useCallback(async () => {
    try {
      const res = await fetch(`${API}/btc/account`, {
        headers: authHeaders(),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Unable to load BTC account");
      }

      if (data.account) {
        setAccount({
          balance: Number(data.account.balance ?? STARTING_BALANCE),
          equity: Number(
            data.account.equity ??
            data.account.balance ??
            STARTING_BALANCE
          ),
          floatingProfit: Number(
            data.account.floatingProfit ?? 0
          ),
          realizedProfit: Number(
            data.account.realizedProfit ?? 0
          ),
        });
      }

      setTrades(Array.isArray(data.trades) ? data.trades : []);

      if (data.quote) {
        applyQuote(data.quote);
      }
    } catch (error) {
      console.error("BTC ACCOUNT ERROR:", error);
      setErrorMessage(error.message || "Unable to load BTC account");
    }
  }, [applyQuote]);

  /* =========================================================
     HISTORY
  ========================================================= */

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);

    try {
      const res = await fetch(`${API}/btc/history`, {
        headers: authHeaders(),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Unable to load trade history");
      }

      setHistory(
        Array.isArray(data.history)
          ? data.history
          : Array.isArray(data.trades)
          ? data.trades
          : []
      );
    } catch (error) {
      console.error("BTC HISTORY ERROR:", error);
      setErrorMessage(error.message || "Unable to load history");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  /* =========================================================
     BTC BOT STATUS
  ========================================================= */

  const loadBotStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/btc/bot/status`, {
        headers: authHeaders(),
      });

      const data = await res.json();

      if (data.success === false) {
        return;
      }

      const enabled = Boolean(
        data.enabled ??
        data.active ??
        data.botEnabled ??
        data.status?.enabled ??
        data.status?.active
      );

      setAiBotEnabled(enabled);

      const botQty = Number(
        data.quantity ??
        data.btcAiBotQuantity ??
        data.status?.quantity ??
        MIN_QTY
      );

      if (botQty >= MIN_QTY) {
        setQuantity(
          Math.min(
            MAX_QTY,
            Math.max(MIN_QTY, botQty)
          )
        );
      }

      if (data.signal) {
        setAiBotSignal(data.signal);
      } else if (data.status?.signal) {
        setAiBotSignal(data.status.signal);
      }
    } catch (error) {
      console.error("BTC BOT STATUS ERROR:", error);
    }
  }, []);

  /* =========================================================
     BOT TOGGLE
  ========================================================= */

  const toggleAiBot = async () => {
    setAiBotLoading(true);
    setErrorMessage("");

    try {
      const endpoint = aiBotEnabled
        ? `${API}/btc/bot/stop`
        : `${API}/btc/bot/start`;

      const options = {
        method: "POST",
        headers: authHeaders(),
      };

      if (!aiBotEnabled) {
        options.body = JSON.stringify({
          quantity: Number(quantity),
        });
      }

      const res = await fetch(endpoint, options);
      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(
          data.message ||
          `Unable to ${aiBotEnabled ? "stop" : "start"} BTC bot`
        );
      }

      const enabled = !aiBotEnabled;

      setAiBotEnabled(enabled);

      if (data.signal) {
        setAiBotSignal(data.signal);
      }

      await loadBotStatus();
      await loadAccount();
    } catch (error) {
      console.error("BTC BOT TOGGLE ERROR:", error);
      setErrorMessage(error.message || "BTC bot action failed");
    } finally {
      setAiBotLoading(false);
    }
  };

  /* =========================================================
     MANUAL QUANTITY
  ========================================================= */

  const changeQuantity = (delta) => {
    setQuantity((old) => {
      const next = Number(old) + Number(delta);

      return Math.min(
        MAX_QTY,
        Math.max(
          MIN_QTY,
          Number(next.toFixed(3))
        )
      );
    });
  };

  /* =========================================================
     OPEN BTC TRADE
  ========================================================= */

  const openTrade = async (side) => {
    if (!price || loading) return;

    setLoading(true);
    setErrorMessage("");

    try {
      const res = await fetch(`${API}/btc/open`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          side,
          quantity: Number(quantity),
        }),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(
          data.message || "Unable to open BTC trade"
        );
      }

      await loadAccount();
      await loadHistory();
    } catch (error) {
      console.error("BTC OPEN ERROR:", error);
      setErrorMessage(
        error.message || "Unable to open BTC trade"
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     CLOSE BTC TRADE
  ========================================================= */

  const closeTrade = async (tradeId) => {
    if (!tradeId || loading) return;

    setLoading(true);
    setErrorMessage("");

    try {
      const res = await fetch(
        `${API}/btc/close/${tradeId}`,
        {
          method: "POST",
          headers: authHeaders(),
        }
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(
          data.message || "Unable to close BTC trade"
        );
      }

      await loadAccount();
      await loadHistory();
    } catch (error) {
      console.error("BTC CLOSE ERROR:", error);
      setErrorMessage(
        error.message || "Unable to close BTC trade"
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     LIVE P/L
  ========================================================= */

  const calculateLivePnl = (trade) => {
    const entry = Number(trade.entryPrice || 0);

    const current =
      trade.side === "BUY"
        ? Number(bid || price)
        : Number(ask || price);

    const qty = Number(
      trade.quantity ??
      trade.qty ??
      0
    );

    if (
      !Number.isFinite(entry) ||
      !Number.isFinite(current) ||
      !Number.isFinite(qty)
    ) {
      return 0;
    }

    if (trade.side === "BUY") {
      return (current - entry) * qty;
    }

    return (entry - current) * qty;
  };

  /* =========================================================
     SOCKET.IO BTC LIVE PRICE
  ========================================================= */

  useEffect(() => {
    const socket = io(
      window.location.origin,
      {
        transports: ["websocket", "polling"],
      }
    );

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("BTC Trading Socket Connected");
    });

    socket.on("btc_price", (quote) => {
      applyQuote(quote);
    });

    socket.on("btc_signal", (signal) => {
      if (signal) {
        setAiBotSignal(signal);
      }
    });

    socket.on("disconnect", () => {
      console.log("BTC Trading Socket Disconnected");
    });

    return () => {
      socket.off("btc_price");
      socket.off("btc_signal");
      socket.disconnect();
      socketRef.current = null;
    };
  }, [applyQuote]);

  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {
    loadAccount();
    loadHistory();
    loadBotStatus();
  }, [loadAccount, loadHistory, loadBotStatus]);

  /* =========================================================
     REFRESH ACCOUNT
  ========================================================= */

  useEffect(() => {
    const timer = setInterval(() => {
      loadAccount();
      loadBotStatus();
    }, 5000);

    return () => clearInterval(timer);
  }, [loadAccount, loadBotStatus]);

  /* =========================================================
     FLOATING P/L
  ========================================================= */

  const liveFloatingPnl = useMemo(() => {
    return trades.reduce(
      (total, trade) =>
        total + calculateLivePnl(trade),
      0
    );
  }, [trades, price, bid, ask]);

  const liveEquity =
    Number(account.balance || STARTING_BALANCE) +
    Number(liveFloatingPnl || 0);

  /* =========================================================
     TRADINGVIEW CHART
  ========================================================= */

  useEffect(() => {
    const container = chartRef.current;

    if (!container) {
      return undefined;
    }

    container.innerHTML = "";

    const widget = document.createElement("div");

    widget.className = "tradingview-widget-container";

    widget.style.width = "100%";
    widget.style.height = "100%";
    widget.style.minHeight = "450px";

    const script = document.createElement("script");

    script.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";

    script.type = "text/javascript";
    script.async = true;

    script.text = JSON.stringify({
      autosize: true,

      symbol: "BINANCE:BTCUSDT",

      interval: "15",

      timezone: "Asia/Kolkata",

      theme: "dark",

      style: "1",

      locale: "en",

      enable_publishing: false,

      allow_symbol_change: false,

      hide_side_toolbar: false,

      withdateranges: true,

      hide_top_toolbar: false,

      hide_legend: false,

      save_image: false,

      studies: [],

      support_host: "https://www.tradingview.com"
    });

    widget.appendChild(script);
    container.appendChild(widget);

    return () => {
      if (container) {
        container.innerHTML = "";
      }
    };
  }, []);

  return (
    <div className="gold-page btc-page">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <header className="gold-header">

        <div className="gold-brand">

          <div className="gold-notification">
            ●
          </div>

          <div>
            <strong>
              Demo Trader
            </strong>

            <small>
              Premium Trader
            </small>
          </div>

          <div className="gold-avatar">
            DT
          </div>

        </div>

      </header>

      {/* =====================================================
          MARKET BAR
      ===================================================== */}

      <section
        id="btc-market-section"
        className="gold-market-bar"
      >

        <div className="gold-symbol">

          <div>
            BTCUSDT <span>☆</span>
          </div>

          <small>
            Bitcoin / US Dollar
          </small>

        </div>

        <div className="gold-main-price">

          <strong>
            {price
              ? money(price)
              : "Loading..."}
          </strong>

          <small>
            LIVE BTCUSDT
          </small>

        </div>

        <div className="gold-market-metric">

          <span>
            24H HIGH
          </span>

          <strong>
            —
          </strong>

        </div>

        <div className="gold-market-metric">

          <span>
            24H LOW
          </span>

          <strong>
            —
          </strong>

        </div>

        <div className="gold-market-metric">

          <span>
            24H CHANGE
          </span>

          <strong className="profit">
            LIVE
          </strong>

        </div>

        <div className="gold-market-metric">

          <span>
            BID
          </span>

          <strong className="sell-text">
            {priceText(bid)}
          </strong>

        </div>

        <div className="gold-market-metric">

          <span>
            ASK
          </span>

          <strong className="profit">
            {priceText(ask)}
          </strong>

        </div>

        <div className="gold-market-metric">

          <span>
            SPREAD
          </span>

          <strong>
            {bid && ask
              ? (
                  Number(ask) -
                  Number(bid)
                ).toFixed(2)
              : "0.00"}
          </strong>

        </div>

      </section>

      {/* =====================================================
          ERROR
      ===================================================== */}

      {errorMessage && (
        <div className="gold-error">
          {errorMessage}
        </div>
      )}

      {/* =====================================================
          ACCOUNT
      ===================================================== */}

      <section className="gold-account-strip">

        <div className="gold-account-card main">

          <span>
            DEMO ACCOUNT
          </span>

          <strong>
            {money(account.balance)}
          </strong>

          <small>
            Demo Balance
          </small>

        </div>

        <div className="gold-account-card">

          <span>
            EQUITY
          </span>

          <strong>
            {money(liveEquity)}
          </strong>

          <small>
            Account Equity
          </small>

        </div>

        <div className="gold-account-card">

          <span>
            FLOATING P/L
          </span>

          <strong
            className={
              Number(liveFloatingPnl) >= 0
                ? "profit"
                : "loss"
            }
          >

            {Number(liveFloatingPnl) >= 0
              ? "+"
              : ""}

            {money(liveFloatingPnl)}

          </strong>

          <small>
            Open Positions
          </small>

        </div>

        <div className="gold-account-card">

          <span>
            REALIZED P/L
          </span>

          <strong
            className={
              Number(account.realizedProfit || 0) >= 0
                ? "profit"
                : "loss"
            }
          >

            {Number(account.realizedProfit || 0) >= 0
              ? "+"
              : ""}

            {money(account.realizedProfit)}

          </strong>

          <small>
            Closed Trades
          </small>

        </div>

      </section>

      {/* =====================================================
          CHART + ORDER
      ===================================================== */}

      <section className="gold-terminal-grid">

        {/* CHART */}

        <div className="gold-chart-card">

          <div className="gold-chart-header">

            <div>

              <strong>
                BTCUSDT
              </strong>

              <span>
                Bitcoin / U.S. Dollar
              </span>

            </div>

            <div className="gold-timeframes">

              <button>
                1m
              </button>

              <button>
                5m
              </button>

              <button className="selected">
                15m
              </button>

              <button>
                1h
              </button>

              <button>
                4h
              </button>

            </div>

          </div>

          <div
            ref={chartRef}
            className="gold-chart"
          />

        </div>

        {/* ORDER PANEL */}

        <div className="gold-order-card">

          <div className="gold-order-tabs">

            <button className="active">
              Market Order
            </button>

            <button>
              Pending Order
            </button>

          </div>

          <div className="gold-order-body">

            <div className="gold-order-title">
              BTC QUANTITY
            </div>

            <div className="gold-lot-control">

              <button
                type="button"
                onClick={() =>
                  changeQuantity(-STEP_QTY)
                }
              >
                −
              </button>

              <strong>
                {Number(quantity).toFixed(3)}
              </strong>

              <button
                type="button"
                onClick={() =>
                  changeQuantity(STEP_QTY)
                }
              >
                +
              </button>

            </div>

            <div className="gold-lot-info">

              <span>
                Min: 0.001
              </span>

              <span>
                Step: 0.001
              </span>

              <span>
                Max: 0.100
              </span>

            </div>

            <div className="gold-order-values">

              <div>

                <span>
                  VALUE
                </span>

                <strong>
                  {price
                    ? money(
                        Number(price) *
                        Number(quantity)
                      )
                    : "--"}
                </strong>

              </div>

              <div>

                <span>
                  MARGIN
                </span>

                <strong>
                  {price
                    ? money(
                        Number(price) *
                        Number(quantity)
                      )
                    : "--"}
                </strong>

              </div>

            </div>

            {/* =================================================
                AI BTC BOT
            ================================================= */}

            <div className="gold-ai-bot-card">

              <div className="gold-ai-bot-head">

                <div>

                  <strong>
                    🤖 AI BTC BOT
                  </strong>

                  <small>
                    BTCUSDT automated demo strategy
                  </small>

                </div>

                <span
                  className={
                    aiBotEnabled
                      ? "gold-ai-status on"
                      : "gold-ai-status off"
                  }
                >
                  {aiBotEnabled
                    ? "● ON"
                    : "● OFF"}
                </span>

              </div>

              {aiBotSignal && (
                <div className="gold-ai-signal">

                  <span>
                    AI SIGNAL
                  </span>

                  <strong
                    className={
                      aiBotSignal.signal === "BUY"
                        ? "profit"
                        : aiBotSignal.signal === "SELL"
                        ? "loss"
                        : ""
                    }
                  >
                    {aiBotSignal.signal || "WAIT"}
                  </strong>

                </div>
              )}

              <button
                type="button"
                className={
                  aiBotEnabled
                    ? "gold-ai-stop"
                    : "gold-ai-start"
                }
                disabled={aiBotLoading}
                onClick={toggleAiBot}
              >
                {aiBotLoading
                  ? "PLEASE WAIT..."
                  : aiBotEnabled
                  ? "🛑 TURN OFF AI BOT"
                  : "🟢 TURN ON AI BOT"}
              </button>

            </div>

            {/* BUY / SELL */}

            <div className="gold-big-buttons">

              <button
                className="gold-buy"
                disabled={
                  loading ||
                  !price
                }
                onClick={() =>
                  openTrade("BUY")
                }
              >

                <span>
                  BUY
                </span>

                <strong>
                  {priceText(
                    ask || price
                  )}
                </strong>

              </button>

              <button
                className="gold-sell"
                disabled={
                  loading ||
                  !price
                }
                onClick={() =>
                  openTrade("SELL")
                }
              >

                <span>
                  SELL
                </span>

                <strong>
                  {priceText(
                    bid || price
                  )}
                </strong>

              </button>

            </div>

            <div className="gold-contract-box">

              <div className="gold-bars">
                ₿
              </div>

              <div>

                <strong>
                  BTC Quantity Based Trading
                </strong>

                <small>
                  0.001 BTC minimum demo order
                </small>

              </div>

            </div>

            <div className="gold-demo-warning">

              <strong>
                DEMO ACCOUNT
              </strong>

              <p>
                Virtual funds only.
                Real wallet funds are
                never affected.
              </p>

            </div>

          </div>

        </div>

      </section>

      {/* =====================================================
          POSITIONS + HISTORY
      ===================================================== */}

      <section className="gold-bottom-grid">

        {/* OPEN POSITIONS */}

        <div className="gold-positions-card">

          <div className="gold-section-title">

            <div>

              <strong>
                OPEN POSITIONS
              </strong>

              <span>
                ({trades.length})
              </span>

            </div>

            <span className="open-badge">
              {trades.length} Open
            </span>

          </div>

          <div className="gold-table-wrap">

            <table className="gold-table">

              <thead>

                <tr>

                  <th>
                    Symbol
                  </th>

                  <th>
                    Type
                  </th>

                  <th>
                    BTC Qty
                  </th>

                  <th>
                    Entry Price
                  </th>

                  <th>
                    Current Price
                  </th>

                  <th>
                    P/L
                  </th>

                  <th>
                    Action
                  </th>

                </tr>

              </thead>

              <tbody>

                {trades.length === 0 ? (

                  <tr>

                    <td
                      colSpan="7"
                      className="gold-no-data"
                    >
                      No open positions
                    </td>

                  </tr>

                ) : (

                  trades.map((trade) => {

                    const pnl =
                      calculateLivePnl(trade);

                    const qty = Number(
                      trade.quantity ??
                      trade.qty ??
                      0
                    );

                    return (

                      <tr
                        key={trade._id}
                      >

                        <td>
                          <strong>
                            BTCUSDT
                          </strong>
                        </td>

                        <td
                          className={
                            trade.side === "BUY"
                              ? "trade-buy"
                              : "trade-sell"
                          }
                        >
                          {trade.side}
                        </td>

                        <td>
                          {qty.toFixed(3)}
                        </td>

                        <td>
                          {priceText(
                            trade.entryPrice
                          )}
                        </td>

                        <td>
                          {priceText(
                            trade.side === "BUY"
                              ? bid || price
                              : ask || price
                          )}
                        </td>

                        <td
                          className={
                            pnl >= 0
                              ? "profit"
                              : "loss"
                          }
                        >

                          {pnl >= 0
                            ? "+"
                            : ""}

                          {money(pnl)}

                        </td>

                        <td>

                          <button
                            className="gold-close-btn"
                            disabled={loading}
                            onClick={() =>
                              closeTrade(
                                trade._id
                              )
                            }
                          >
                            CLOSE
                          </button>

                        </td>

                      </tr>

                    );
                  })

                )}

              </tbody>

            </table>

          </div>

          <div className="gold-floating-footer">

            <span>
              Total Floating P/L
            </span>

            <strong
              className={
                Number(liveFloatingPnl) >= 0
                  ? "profit"
                  : "loss"
              }
            >

              {Number(liveFloatingPnl) >= 0
                ? "+"
                : ""}

              {money(liveFloatingPnl)}

            </strong>

          </div>

        </div>

        {/* HISTORY */}

        <div
          id="btc-history"
          className="gold-history-card"
        >

          <div className="gold-section-title">

            <div>

              <strong>
                TRADE HISTORY
              </strong>

            </div>

            <button
              className="history-view"
              onClick={loadHistory}
              disabled={historyLoading}
            >
              {historyLoading
                ? "Loading..."
                : "Refresh"}
            </button>

          </div>

          <div className="gold-table-wrap">

            <table className="gold-table">

              <thead>

                <tr>

                  <th>
                    Symbol
                  </th>

                  <th>
                    Type
                  </th>

                  <th>
                    BTC Qty
                  </th>

                  <th>
                    Entry
                  </th>

                  <th>
                    Exit
                  </th>

                  <th>
                    P/L
                  </th>

                  <th>
                    Time
                  </th>

                </tr>

              </thead>

              <tbody>

                {history.length === 0 ? (

                  <tr>

                    <td
                      colSpan="7"
                      className="gold-no-data"
                    >
                      No trades yet
                    </td>

                  </tr>

                ) : (

                  history.map((trade) => {

                    const pnl = Number(
                      trade.profitLoss || 0
                    );

                    const qty = Number(
                      trade.quantity ??
                      trade.qty ??
                      0
                    );

                    return (

                      <tr
                        key={trade._id}
                      >

                        <td>

                          <strong>
                            BTCUSDT
                          </strong>

                        </td>

                        <td
                          className={
                            trade.side === "BUY"
                              ? "trade-buy"
                              : "trade-sell"
                          }
                        >
                          {trade.side}
                        </td>

                        <td>
                          {qty.toFixed(3)}
                        </td>

                        <td>
                          {priceText(
                            trade.entryPrice
                          )}
                        </td>

                        <td>
                          {trade.exitPrice
                            ? priceText(
                                trade.exitPrice
                              )
                            : "--"}
                        </td>

                        <td
                          className={
                            pnl >= 0
                              ? "profit"
                              : "loss"
                          }
                        >

                          {pnl >= 0
                            ? "+"
                            : ""}

                          {money(pnl)}

                        </td>

                        <td>

                          {trade.closedAt
                            ? new Date(
                                trade.closedAt
                              ).toLocaleString(
                                "en-IN",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )
                            : "--"}

                        </td>

                      </tr>

                    );
                  })

                )}

              </tbody>

            </table>

          </div>

        </div>

      </section>

      {/* =====================================================
          RULES
      ===================================================== */}

      <section
        id="btc-rules"
        className="gold-rules-card"
      >

        <div className="gold-section-title">

          <div>

            <strong>
              BTC DEMO TRADING RULES
            </strong>

          </div>

        </div>

        <div className="gold-rule-grid">

          <div>

            <span>
              STARTING BALANCE
            </span>

            <strong>
              $10,000
            </strong>

          </div>

          <div>

            <span>
              SYMBOL
            </span>

            <strong>
              BTCUSDT
            </strong>

          </div>

          <div>

            <span>
              MIN QUANTITY
            </span>

            <strong>
              0.001 BTC
            </strong>

          </div>

          <div>

            <span>
              MAX QUANTITY
            </span>

            <strong>
              0.100 BTC
            </strong>

          </div>

        </div>

        <p className="gold-disclaimer">
          This is a virtual demo
          trading environment.
          All balances and profits
          are simulated and do not
          represent real funds.
        </p>

      </section>

      {/* =====================================================
          TICKER
      ===================================================== */}

      <div className="gold-ticker">

        <div>

          <strong>
            BTCUSDT
          </strong>

          <span>
            {priceText(price)}
          </span>

          <b>
            LIVE
          </b>

        </div>

        <div>

          <strong>
            XAUUSD
          </strong>

          <span>
            —
          </span>

        </div>

        <div>

          <strong>
            ETHUSDT
          </strong>

          <span>
            —
          </span>

        </div>

        <div>

          <strong>
            SOLUSDT
          </strong>

          <span>
            —
          </span>

        </div>

        <div>

          <strong>
            BNBUSDT
          </strong>

          <span>
            —
          </span>

        </div>

        <small>
          Powered by TradingView
        </small>

      </div>

    </div>
  );
}
